import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Tabs,
  Tab,
  LinearProgress,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Stat card component
const StatCard = ({ icon, label, value, color = 'primary', subtitle }) => (
  <Card variant="outlined">
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ color: `${color}.main`, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight={600}>{value?.toLocaleString() || '0'}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </CardContent>
  </Card>
);

const FTCData = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Stats
  const [stats, setStats] = useState(null);
  const [recentImports, setRecentImports] = useState([]);
  const [categories, setCategories] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [minComplaints, setMinComplaints] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Import dialog state
  const [importDialog, setImportDialog] = useState(false);
  const [importStep, setImportStep] = useState(0); // 0: upload, 1: map columns, 2: confirm
  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    phone_number: '',
    category: '',
    report_date: '',
  });

  // Import history
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // API sync state
  const [apiConfigured, setApiConfigured] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncDialog, setSyncDialog] = useState(false);
  const [syncMode, setSyncMode] = useState('latest');
  const [syncDays, setSyncDays] = useState(7);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [autoSyncToggling, setAutoSyncToggling] = useState(false);

  // Sync progress polling
  const [syncProgress, setSyncProgress] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadStats();
    loadCategories();
  }, []);

  useEffect(() => {
    if (tabValue === 2) {
      loadImportHistory();
    }
  }, [tabValue]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFTCStats();
      setStats(response.data.stats);
      setRecentImports(response.data.recent_imports || []);
      setApiConfigured(response.data.api_configured || false);
      if (response.data.auto_sync_enabled !== undefined) {
        setAutoSyncEnabled(response.data.auto_sync_enabled);
      }
    } catch (err) {
      console.error('Failed to load FTC stats:', err);
      setError('Failed to load FTC statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await adminAPI.getFTCCategories();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadImportHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await adminAPI.getFTCImportHistory({ limit: 50 });
      setImportHistory(response.data.imports || []);
    } catch (err) {
      console.error('Failed to load import history:', err);
      setError('Failed to load import history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await adminAPI.getSyncProgress();
        const data = res.data;
        setSyncProgress(data.job);

        if (!data.running) {
          // Sync finished
          stopPolling();
          setSyncLoading(false);

          if (data.finalResult?.success) {
            const s = data.finalResult.stats || data.finalResult.totals || {};
            const extra = data.finalResult.dateStr ? ` (${data.finalResult.dateStr})` : '';
            const daysInfo = data.finalResult.daysSuccessful !== undefined
              ? ` — ${data.finalResult.daysSuccessful}/${data.finalResult.daysAttempted} days`
              : '';
            setSuccess(`Sync complete${extra}${daysInfo}: ${s.inserted || 0} inserted, ${s.updated || 0} updated`);
            setSyncDialog(false);
            setSyncProgress(null);
            loadStats();
            if (tabValue === 2) loadImportHistory();
          } else if (data.finalResult) {
            setError(data.finalResult.error || 'Sync failed');
            setSyncProgress(null);
          }
        }
      } catch {
        // Polling error — keep trying
      }
    }, 2000);
  }, [stopPolling, tabValue]);

  const handleApiSync = async () => {
    try {
      setSyncLoading(true);
      setSyncProgress(null);
      setError('');

      const params = { mode: syncMode, source: 'csv' };
      if (syncMode === 'backfill') {
        params.days = syncDays;
      }

      const response = await adminAPI.triggerFTCSync(params);

      if (response.data.started) {
        // Sync started in background — begin polling
        startPolling();
      } else {
        setError(response.data.error || 'Failed to start sync');
        setSyncLoading(false);
      }
    } catch (err) {
      console.error('FTC sync failed:', err);
      setError(err.response?.data?.error || 'FTC sync failed');
      setSyncLoading(false);
    }
  };

  const handleToggleAutoSync = async () => {
    try {
      setAutoSyncToggling(true);
      const newValue = !autoSyncEnabled;
      await adminAPI.updateFeatureFlag('ftc_auto_sync_enabled', { value: newValue });
      setAutoSyncEnabled(newValue);
      setSuccess(newValue ? 'Auto sync enabled — FTC data will sync daily at 1 PM ET' : 'Auto sync disabled');
    } catch (err) {
      console.error('Failed to toggle auto sync:', err);
      setError('Failed to update auto sync setting');
    } finally {
      setAutoSyncToggling(false);
    }
  };

  const handleCancelSync = async () => {
    try {
      const response = await adminAPI.cancelFTCSync();
      if (response.data.success) {
        stopPolling();
        setSyncLoading(false);
        setSyncProgress(null);
        setSuccess(response.data.message);
      }
    } catch (err) {
      console.error('Cancel sync failed:', err);
      setError(err.response?.data?.error || 'Failed to cancel sync');
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestLoading(true);
      setTestResult(null);
      setError('');
      const response = await adminAPI.testFTCApi();
      setTestResult(response.data);
      if (response.data.success) {
        setSuccess(`API connection OK — ${response.data.totalRecords} total records in FTC database`);
      } else {
        setError(`API test failed: ${response.data.message}`);
      }
    } catch (err) {
      console.error('API test failed:', err);
      setError(err.response?.data?.error || 'API connection test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setSearchLoading(true);
      setError('');
      const response = await adminAPI.searchFTCNumbers({
        query: searchQuery || undefined,
        minComplaints: minComplaints || undefined,
        category: categoryFilter || undefined,
        limit: 100,
      });
      setSearchResults(response.data.numbers || []);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportText(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleParseHeaders = async () => {
    if (!importText.trim()) {
      setError('Please provide CSV data');
      return;
    }

    try {
      setImportLoading(true);
      setError('');
      
      const response = await adminAPI.parseCSVHeaders(importText);
      
      if (response.data.success) {
        setCsvHeaders(response.data.headers);
        
        // Auto-detect common column names
        const autoMapping = { phone_number: '', category: '', report_date: '' };
        response.data.headers.forEach(h => {
          const name = h.name.toLowerCase();
          if (name.includes('phone') || name.includes('number') || name.includes('caller')) {
            autoMapping.phone_number = h.index.toString();
          }
          if (name.includes('subject') || name.includes('category') || name.includes('type')) {
            autoMapping.category = h.index.toString();
          }
          if (name.includes('date') || name.includes('created')) {
            autoMapping.report_date = h.index.toString();
          }
        });
        setColumnMapping(autoMapping);
        setImportStep(1);
      } else {
        setError(response.data.error || 'Failed to parse headers');
      }
    } catch (err) {
      console.error('Parse headers failed:', err);
      setError(err.response?.data?.error || 'Failed to parse CSV headers');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async () => {
    if (!columnMapping.phone_number) {
      setError('Phone number column is required');
      return;
    }

    try {
      setImportLoading(true);
      setError('');
      
      // Convert string indices to numbers
      const mapping = {
        phone_number: parseInt(columnMapping.phone_number),
        category: columnMapping.category ? parseInt(columnMapping.category) : undefined,
        report_date: columnMapping.report_date ? parseInt(columnMapping.report_date) : undefined,
      };
      
      const response = await adminAPI.importFTCData(
        importText,
        importFile?.name || 'manual_paste',
        mapping
      );

      if (response.data.success) {
        setSuccess(`Import complete: ${response.data.stats.inserted} inserted, ${response.data.stats.updated} updated, ${response.data.stats.skipped} skipped`);
        handleCloseImport();
        loadStats();
      } else {
        setError(response.data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Import failed:', err);
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const handleCloseImport = () => {
    setImportDialog(false);
    setImportStep(0);
    setImportText('');
    setImportFile(null);
    setCsvHeaders([]);
    setColumnMapping({ phone_number: '', category: '', report_date: '' });
  };

  const formatCategory = (category) => {
    if (!category) return '-';
    return category
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Completed" size="small" color="success" />;
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Failed" size="small" color="error" />;
      case 'running':
        return <Chip icon={<ScheduleIcon />} label="Running" size="small" color="warning" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getRowCount = () => {
    if (!importText) return 0;
    const lines = importText.split('\n').filter(l => l.trim());
    return Math.max(0, lines.length - 1); // Subtract header row
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          FTC Complaint Data
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadStats}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialog(true)}
            size="small"
          >
            Import Data
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<AssessmentIcon />} label="Overview" />
          <Tab icon={<SearchIcon />} label="Search" />
          <Tab icon={<HistoryIcon />} label="Import History" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<PhoneIcon fontSize="large" />}
                  label="Phone Numbers"
                  value={stats?.total_numbers}
                  color="primary"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<WarningIcon fontSize="large" />}
                  label="Total Complaints"
                  value={stats?.total_complaints}
                  color="warning"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<ErrorIcon fontSize="large" />}
                  label="High Complaint (10+)"
                  value={stats?.high_complaint_numbers}
                  color="error"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<HistoryIcon fontSize="large" />}
                  label="Last Import"
                  value={stats?.last_import ? format(new Date(stats.last_import), 'MMM d') : 'Never'}
                  color="info"
                  subtitle={stats?.last_import ? format(new Date(stats.last_import), 'h:mm a') : ''}
                />
              </Grid>
            </Grid>

            {/* Categories Breakdown */}
            {categories.length > 0 && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Categories Breakdown</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {categories.slice(0, 15).map((cat) => (
                    <Chip
                      key={cat.category}
                      label={`${formatCategory(cat.category)} (${cat.count})`}
                      variant="outlined"
                      onClick={() => {
                        setCategoryFilter(cat.category);
                        setTabValue(1);
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* FTC Data Sync */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">FTC Data Sync</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {syncLoading && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleCancelSync}
                      startIcon={<StopIcon />}
                    >
                      Force Stop
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSyncDialog(true)}
                    disabled={syncLoading}
                    startIcon={syncLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                  >
                    Sync Now
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                  Downloads complaint data CSV files directly from the FTC website. Files are published every weekday by ~noon ET.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoSyncEnabled}
                      onChange={handleToggleAutoSync}
                      disabled={autoSyncToggling}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Daily auto-sync {autoSyncEnabled ? '(1 PM ET)' : '(off)'}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ ml: 2, mr: 0 }}
                />
              </Box>
            </Paper>

            {/* Recent Imports */}
            {recentImports.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Recent Imports</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Source</TableCell>
                        <TableCell align="right">Processed</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Inserted</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Updated</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentImports.map((imp) => (
                        <TableRow key={imp.id}>
                          <TableCell>{format(new Date(imp.started_at), 'MMM d, h:mm a')}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{imp.source_file || imp.import_type}</TableCell>
                          <TableCell align="right">{imp.records_processed?.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{imp.records_inserted?.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{imp.records_updated?.toLocaleString()}</TableCell>
                          <TableCell>{getStatusChip(imp.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </TabPanel>

      {/* Search Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Phone Number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by phone number..."
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Min Complaints"
                type="number"
                value={minComplaints}
                onChange={(e) => setMinComplaints(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.category} value={cat.category}>
                    {formatCategory(cat.category)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={searchLoading}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {searchResults.length > 0 && (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Phone Number</TableCell>
                    <TableCell align="right">FTC Complaints</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Categories</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>First Reported</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Last Reported</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((num) => (
                    <TableRow key={num.phone_number}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {num.phone_number}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={num.ftc_complaint_count}
                          size="small"
                          color={num.ftc_complaint_count >= 10 ? 'error' : num.ftc_complaint_count >= 5 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {num.ftc_categories?.slice(0, 3).map((cat, idx) => (
                            <Chip key={idx} label={formatCategory(cat)} size="small" variant="outlined" />
                          ))}
                          {num.ftc_categories?.length > 3 && (
                            <Chip label={`+${num.ftc_categories.length - 3}`} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {num.ftc_first_reported ? format(new Date(num.ftc_first_reported), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {num.ftc_last_reported ? format(new Date(num.ftc_last_reported), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        {num.known_scam && <Chip label="Known Scam" size="small" color="error" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Showing {searchResults.length} results
              </Typography>
            </Box>
          </Paper>
        )}

        {searchResults.length === 0 && !searchLoading && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              Search for phone numbers with FTC complaints
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Import History Tab */}
      <TabPanel value={tabValue} index={2}>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Source File</TableCell>
                    <TableCell align="right">Processed</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Inserted</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Updated</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Skipped</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importHistory.map((imp) => (
                    <TableRow key={imp.id}>
                      <TableCell>{format(new Date(imp.started_at), 'MMM d, yyyy h:mm a')}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip label={imp.import_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{imp.source_file || '-'}</TableCell>
                      <TableCell align="right">{imp.records_processed?.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{imp.records_inserted?.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{imp.records_updated?.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{imp.records_skipped?.toLocaleString()}</TableCell>
                      <TableCell>{getStatusChip(imp.status)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {imp.completed_at
                          ? `${Math.round((new Date(imp.completed_at) - new Date(imp.started_at)) / 1000)}s`
                          : '-'
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {importHistory.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No import history</Typography>
              </Box>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Import Dialog with Steps */}
      <Dialog open={importDialog} onClose={handleCloseImport} maxWidth="md" fullWidth>
        <DialogTitle>Import FTC Data</DialogTitle>
        <DialogContent>
          <Stepper activeStep={importStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Upload CSV</StepLabel>
            </Step>
            <Step>
              <StepLabel>Map Columns</StepLabel>
            </Step>
            <Step>
              <StepLabel>Import</StepLabel>
            </Step>
          </Stepper>

          {/* Step 0: Upload */}
          {importStep === 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <input
                  type="file"
                  accept=".csv,.txt"
                  id="ftc-file-input"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <label htmlFor="ftc-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ py: 2 }}
                  >
                    {importFile ? importFile.name : 'Choose CSV File'}
                  </Button>
                </label>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or paste CSV data directly:
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={8}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste CSV data here..."
                sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />

              {importText && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {getRowCount()} data rows detected
                </Typography>
              )}
            </>
          )}

          {/* Step 1: Map Columns */}
          {importStep === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Map your CSV columns to the required fields. Only Phone Number is required.
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Phone Number Column *</InputLabel>
                    <Select
                      value={columnMapping.phone_number}
                      label="Phone Number Column *"
                      onChange={(e) => setColumnMapping({ ...columnMapping, phone_number: e.target.value })}
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {csvHeaders.map((h) => (
                        <MenuItem key={h.index} value={h.index.toString()}>
                          {h.index}: {h.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Category Column</InputLabel>
                    <Select
                      value={columnMapping.category}
                      label="Category Column"
                      onChange={(e) => setColumnMapping({ ...columnMapping, category: e.target.value })}
                    >
                      <MenuItem value="">-- None --</MenuItem>
                      {csvHeaders.map((h) => (
                        <MenuItem key={h.index} value={h.index.toString()}>
                          {h.index}: {h.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Report Date Column</InputLabel>
                    <Select
                      value={columnMapping.report_date}
                      label="Report Date Column"
                      onChange={(e) => setColumnMapping({ ...columnMapping, report_date: e.target.value })}
                    >
                      <MenuItem value="">-- None --</MenuItem>
                      {csvHeaders.map((h) => (
                        <MenuItem key={h.index} value={h.index.toString()}>
                          {h.index}: {h.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>CSV Headers Preview:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {csvHeaders.map((h) => (
                    <Chip
                      key={h.index}
                      label={`${h.index}: ${h.name}`}
                      size="small"
                      variant="outlined"
                      color={
                        columnMapping.phone_number === h.index.toString() ? 'primary' :
                        columnMapping.category === h.index.toString() ? 'secondary' :
                        columnMapping.report_date === h.index.toString() ? 'info' : 'default'
                      }
                    />
                  ))}
                </Box>
              </Paper>
            </>
          )}

          {/* Step 2: Confirm */}
          {importStep === 2 && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Ready to import! Review your settings below.
              </Alert>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Rows to process:</Typography>
                    <Typography variant="h6">{getRowCount().toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Source file:</Typography>
                    <Typography variant="h6">{importFile?.name || 'Manual paste'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Column mapping:</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={`Phone: ${csvHeaders.find(h => h.index.toString() === columnMapping.phone_number)?.name || '-'}`} 
                        size="small" 
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                      {columnMapping.category && (
                        <Chip 
                          label={`Category: ${csvHeaders.find(h => h.index.toString() === columnMapping.category)?.name || '-'}`} 
                          size="small" 
                          color="secondary"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {columnMapping.report_date && (
                        <Chip 
                          label={`Date: ${csvHeaders.find(h => h.index.toString() === columnMapping.report_date)?.name || '-'}`} 
                          size="small" 
                          color="info"
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {importLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Processing import...
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImport}>Cancel</Button>
          
          {importStep > 0 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setImportStep(importStep - 1)}
              disabled={importLoading}
            >
              Back
            </Button>
          )}
          
          {importStep === 0 && (
            <Button
              variant="contained"
              endIcon={importLoading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
              onClick={handleParseHeaders}
              disabled={importLoading || !importText.trim()}
            >
              Next
            </Button>
          )}
          
          {importStep === 1 && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setImportStep(2)}
              disabled={!columnMapping.phone_number}
            >
              Next
            </Button>
          )}
          
          {importStep === 2 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleImport}
              disabled={importLoading}
              startIcon={importLoading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              Import
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sync Dialog */}
      <Dialog open={syncDialog} onClose={() => !syncLoading && setSyncDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sync FTC Complaint Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Downloads complaint CSV files directly from the FTC website. No API key required.
          </Typography>

          <FormControl fullWidth sx={{ mt: 1 }} disabled={syncLoading}>
            <InputLabel>Sync Mode</InputLabel>
            <Select
              value={syncMode}
              label="Sync Mode"
              onChange={(e) => setSyncMode(e.target.value)}
            >
              <MenuItem value="latest">Latest Available</MenuItem>
              <MenuItem value="backfill">Backfill (Multiple Days)</MenuItem>
            </Select>
          </FormControl>

          {syncMode === 'backfill' && (
            <TextField
              fullWidth
              label="Number of Days"
              type="number"
              value={syncDays}
              onChange={(e) => setSyncDays(parseInt(e.target.value) || 7)}
              sx={{ mt: 2 }}
              inputProps={{ min: 1, max: 30 }}
              helperText="Download CSVs for the last N weekdays (max 30)"
              disabled={syncLoading}
            />
          )}

          {!syncLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {syncMode === 'latest'
                ? "Downloads the most recently published FTC complaint file. Files are published weekdays by ~noon ET."
                : `Downloads complaint files for the last ${syncDays} days (weekdays only). This may take a minute.`
              }
            </Alert>
          )}

          {/* Live sync progress */}
          {syncLoading && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <CircularProgress size={20} />
                <Typography variant="subtitle2">
                  {syncProgress?.status === 'running' ? 'Importing...' : 'Starting sync...'}
                </Typography>
              </Box>

              {syncProgress && (
                <>
                  {syncProgress.source_file && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {syncProgress.source_file}
                    </Typography>
                  )}

                  <Box sx={{ mb: 1 }}>
                    <LinearProgress
                      variant="indeterminate"
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                      <strong>{(syncProgress.records_processed || 0).toLocaleString()}</strong> processed
                    </Typography>
                    {syncProgress.records_inserted > 0 && (
                      <Typography variant="body2" color="success.main">
                        <strong>{syncProgress.records_inserted.toLocaleString()}</strong> new
                      </Typography>
                    )}
                    {syncProgress.records_updated > 0 && (
                      <Typography variant="body2" color="info.main">
                        <strong>{syncProgress.records_updated.toLocaleString()}</strong> updated
                      </Typography>
                    )}
                    {syncProgress.records_skipped > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>{syncProgress.records_skipped.toLocaleString()}</strong> skipped
                      </Typography>
                    )}
                  </Box>

                  {syncProgress.started_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Started {format(new Date(syncProgress.started_at), 'h:mm:ss a')}
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog(false)} disabled={syncLoading}>Cancel</Button>
          {syncLoading ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelSync}
              startIcon={<StopIcon />}
            >
              Force Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleApiSync}
              startIcon={<RefreshIcon />}
            >
              Start Sync
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FTCData;
